import React from 'react';

const REFERENCE_PORTFOLIOS = [
  { value: 'Cash_Index', label: 'Cash Index (0% Growth Assets)' },
  { value: 'Conservative_Index', label: 'Conservative Index (30% Growth Assets)' },
  { value: 'Conservative_Balanced_Index', label: 'Conservative Balanced Index (50% Growth Assets)' },
  { value: 'Balanced_Index', label: 'Balanced Index (70% Growth Assets)' },
  { value: 'Growth_Index', label: 'Growth Index (90% Growth Assets)' },
];


const InfoIcon = ({ tooltip }) => {
  const [show, setShow] = React.useState(false);
  const timerRef = React.useRef(null);
  const handleEnter = () => {
    timerRef.current = setTimeout(() => setShow(true), 100);
  };
  const handleLeave = () => {
    clearTimeout(timerRef.current);
    setShow(false);
  };
  return (
    <span
      className="info-icon-trigger"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      i
      {show && (
        <div className="info-icon-tooltip">
          {tooltip}
        </div>
      )}
    </span>
  );
};

const SectionBanner = ({ icon, title, color, tooltip }) => {
  const [show, setShow] = React.useState(false);
  const timerRef = React.useRef(null);
  const handleEnter = () => {
    timerRef.current = setTimeout(() => setShow(true), 100);
  };
  const handleLeave = () => {
    clearTimeout(timerRef.current);
    setShow(false);
  };
  return (
    <div className="section-banner" style={{ '--banner-bg': color }}>
      <i className={icon}></i>
      <span className="banner-title">
        {title}
      </span>
      {tooltip && (
        <span
          className="info-icon-trigger"
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        >
          i
          {show && (
            <div className="info-icon-tooltip">
              {tooltip}
            </div>
          )}
        </span>
      )}
    </div>
  );
};

const IncomeRow = ({ label, amountKey, yearsKey, entireKey, yearsLabel, amountTooltip, yearsTooltip, formData, updateFormData }) => (
  <div className="row mb-3 align-items-end">
    <div className="col-md-5">
      <label className="form-label small fw-semibold" style={{ color: '#072040' }}>
        {label}
        <InfoIcon tooltip={amountTooltip} />
      </label>
      <input
        type="number"
        className="form-control"
        value={formData[amountKey] || ''}
        placeholder="0"
        min="0"
        step="1000"
        onChange={(e) => updateFormData({ [amountKey]: parseFloat(e.target.value) || 0 })}
      />
    </div>
    <div className="col-md-4">
      {yearsLabel && (
        <label className="form-label small fw-semibold" style={{ color: '#072040' }}>
          {yearsLabel}
          <InfoIcon tooltip={yearsTooltip} />
        </label>
      )}
      <input
        type="number"
        className="form-control"
        value={formData[yearsKey] || ''}
        placeholder="0"
        min="0"
        max="50"
        disabled={formData[entireKey]}
        onChange={(e) => updateFormData({ [yearsKey]: parseInt(e.target.value) || 0 })}
      />
    </div>
    <div className="col-md-3 d-flex align-items-center" style={{ paddingBottom: 4 }}>
      <div className="form-check">
        <input
          type="checkbox"
          className="form-check-input"
          id={entireKey}
          checked={formData[entireKey] || false}
          onChange={(e) => updateFormData({ [entireKey]: e.target.checked })}
        />
        <label className="form-check-label small" htmlFor={entireKey}>
          Entire retirement
        </label>
      </div>
    </div>
  </div>
);

const CURRENT_YEAR = new Date().getFullYear();

const StepPersonalDetails = ({ formData, updateFormData, nextStep, prevStep, isLocked = false, hiddenFields = [] }) => {
  const isCouple = formData.couple === 'Yes';

  // Calculate ages from birth years (with sensible bounds)
  const defaultBirthYear = CURRENT_YEAR - 67;
  const birthYear1 = formData.birth_year_1 && formData.birth_year_1 > 1900 ? formData.birth_year_1 : defaultBirthYear;
  const birthYear2 = formData.birth_year_2 && formData.birth_year_2 > 1900 ? formData.birth_year_2 : defaultBirthYear;
  const age1Calc = CURRENT_YEAR - birthYear1;
  const age2Calc = CURRENT_YEAR - birthYear2;

  // Determine older age for retirement logic (auto-sorted)
  const olderAge = isCouple ? Math.max(age1Calc, age2Calc) : age1Calc;
  const youngerAge = isCouple ? Math.min(age1Calc, age2Calc) : age1Calc;

  const retirementAge = formData.retirement_age || 67;
  const isAlreadyRetired = retirementAge <= olderAge;

  const handleCoupleChange = (value) => {
    const updates = { couple: value };
    if (value === 'No') {
      updates.birth_year_2 = formData.birth_year_1;
    }
    updateFormData(updates);
  };

  const handleRetirementAgeChange = (value) => {
    // Allow empty string while typing
    if (value === '') {
      updateFormData({ retirement_age: '' });
      return;
    }
    // Only allow numeric input
    if (!/^\d*$/.test(value)) return;
    updateFormData({ retirement_age: value });
  };

  const handleRetirementAgeBlur = () => {
    const value = formData.retirement_age;
    if (value === '' || value === undefined) {
      updateFormData({ retirement_age: olderAge });
      return;
    }
    const parsed = parseInt(value);
    if (isNaN(parsed)) {
      updateFormData({ retirement_age: olderAge });
      return;
    }
    // Clamp between olderAge and 100
    const clamped = Math.max(olderAge, Math.min(100, parsed));
    updateFormData({ retirement_age: clamped });
  };

  const handleBirthYear1Change = (value) => {
    // Allow empty string while typing
    if (value === '') {
      updateFormData({ birth_year_1: '' });
      return;
    }
    const parsed = parseInt(value);
    if (isNaN(parsed)) return;
    const newAge = CURRENT_YEAR - parsed;
    const updates = { birth_year_1: parsed };
    if (newAge >= 18 && newAge <= 100 && retirementAge < newAge) {
      updates.retirement_age = newAge;
    }
    updateFormData(updates);
  };

  const handleBirthYear2Change = (value) => {
    // Allow empty string while typing
    if (value === '') {
      updateFormData({ birth_year_2: '' });
      return;
    }
    const parsed = parseInt(value);
    if (isNaN(parsed)) return;
    updateFormData({ birth_year_2: parsed });
  };

  React.useEffect(() => {
    if (isAlreadyRetired && formData.annual_contributions !== 0) {
      updateFormData({ annual_contributions: 0 });
    }
  }, [isAlreadyRetired]);

  // Auto-adjust retirement age if older person's age increases past it (via birth year change)
  // Only runs when olderAge changes, not when user edits retirement age directly
  const prevOlderAgeRef = React.useRef(olderAge);
  React.useEffect(() => {
    const prevOlderAge = prevOlderAgeRef.current;
    prevOlderAgeRef.current = olderAge;
    // Only auto-adjust if olderAge actually increased (birth year changed)
    if (olderAge > prevOlderAge && olderAge >= 18 && olderAge <= 100) {
      const currentRetAge = parseInt(formData.retirement_age) || 0;
      if (currentRetAge < olderAge) {
        updateFormData({ retirement_age: olderAge });
      }
    }
  }, [olderAge]);

  const handleAnnuityTypeChange = (e) => {
    const value = e.target.value;
    if (value !== 'market_linked') {
      updateFormData({
        annuity_type: value,
        annuity_reference_portfolio: '',
        accelerated_reduction_rate: 0,
      });
    } else {
      updateFormData({ annuity_type: value });
    }
  };

  const rentalYears = parseInt(formData.rental_income_years) || 0;
  const showPropertyReminder = rentalYears > 0 && !formData.rental_entire_retirement;

  return (
    <div className="retirement-form">
      {isLocked && (
        <div className="alert alert-warning mb-3 d-flex align-items-center">
          <i className="bi bi-lock-fill me-2"></i>
          <span>
            <strong>View Only:</strong> This scenario is locked. Click "Make Changes" above to edit.
          </span>
        </div>
      )}
      <fieldset disabled={isLocked} style={{ opacity: isLocked ? 0.7 : 1 }}>
      {/* ─── Personal Details ─── */}
      <SectionBanner
        icon="bi bi-person-fill"
        title="Personal Details"
        color="#072040"
      />

      {/* Row 1: Couple + Homeowner + Birth Years */}
      {(!hiddenFields.includes('couple') || !hiddenFields.includes('homeowner') || !hiddenFields.includes('birth_year')) && (
      <div className="row mb-2">
        {!hiddenFields.includes('couple') && (
        <div className="col-md-3">
          <label htmlFor="couple" className="form-label small fw-semibold" style={{ color: '#072040' }}>
            Couple
            <InfoIcon tooltip="Select 'Yes' if they are married or in a de facto relationship. This affects age pension calculations and tax treatment." />
          </label>
          <select
            id="couple"
            className="form-select form-control"
            value={formData.couple || 'Yes'}
            onChange={(e) => handleCoupleChange(e.target.value)}
          >
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>
        )}
        {!hiddenFields.includes('homeowner') && (
        <div className="col-md-3">
          <label htmlFor="homeowner" className="form-label small fw-semibold" style={{ color: '#072040' }}>
            Homeowner
            <InfoIcon tooltip="Select 'Yes' if they own their principal place of residence. The family home is generally exempt from the age pension assets test." />
          </label>
          <select
            id="homeowner"
            className="form-select form-control"
            value={formData.homeowner || 'Yes'}
            onChange={(e) => updateFormData({ homeowner: e.target.value })}
          >
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>
        )}
        {!hiddenFields.includes('birth_year') && (
        <>
        <div className="col-md-3">
          <label htmlFor="birth_year_1" className="form-label small fw-semibold" style={{ color: '#072040' }}>
            {isCouple ? 'Partner 1 Birth Year' : 'Birth Year'}
            <InfoIcon tooltip="Birth year is used to calculate current age for pension eligibility. Age pension starts at 67." />
          </label>
          <input
            type="number"
            className="form-control"
            id="birth_year_1"
            min={CURRENT_YEAR - 100}
            max={CURRENT_YEAR - 18}
            value={formData.birth_year_1 === '' ? '' : (formData.birth_year_1 || defaultBirthYear)}
            onChange={(e) => handleBirthYear1Change(e.target.value)}
          />
          <small className="text-muted">Age: {age1Calc}</small>
        </div>
        {isCouple && (
          <div className="col-md-3">
            <label htmlFor="birth_year_2" className="form-label small fw-semibold" style={{ color: '#072040' }}>
              Partner 2 Birth Year
              <InfoIcon tooltip="Birth year of the second partner. The app automatically determines who is older for pension calculations." />
            </label>
            <input
              type="number"
              className="form-control"
              id="birth_year_2"
              min={CURRENT_YEAR - 100}
              max={CURRENT_YEAR - 18}
              value={formData.birth_year_2 === '' ? '' : (formData.birth_year_2 || defaultBirthYear)}
              onChange={(e) => handleBirthYear2Change(e.target.value)}
            />
            <small className="text-muted">Age: {age2Calc}</small>
          </div>
        )}
        </>
        )}
      </div>
      )}

      {!hiddenFields.includes('couple') && !hiddenFields.includes('birth_year') && isCouple && age1Calc !== age2Calc && (
        <div className="alert alert-light small py-2 mb-2" style={{ borderLeft: '3px solid #599BE3' }}>
          <i className="bi bi-info-circle me-2"></i>
          <strong>Pension calculation:</strong> Older partner ({olderAge}) determines retirement timeline.
          Younger partner ({youngerAge}) reaches pension age {youngerAge < 67 ? `in ${67 - youngerAge} years` : 'already eligible'}.
          Full couple pension applies when both are 67+.
        </div>
      )}

      {/* Row 2: Retirement Age + Years + Annual Contributions */}
      <div className="row mb-2">
        {!hiddenFields.includes('retirement_age') && (
        <div className="col-md-3">
          <label htmlFor="retirement_age" className="form-label small fw-semibold" style={{ color: '#072040' }}>
            Retirement Age
            <InfoIcon tooltip="Age at which they plan to retire. If already retired, set this to their current age. Portfolio withdrawals will begin at this age." />
          </label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            className="form-control"
            id="retirement_age"
            value={formData.retirement_age ?? ''}
            onChange={(e) => handleRetirementAgeChange(e.target.value)}
            onBlur={handleRetirementAgeBlur}
          />
        </div>
        )}
        {!hiddenFields.includes('years') && (
        <div className="col-md-3">
          <label htmlFor="years" className="form-label small fw-semibold" style={{ color: '#072040' }}>
            Years in Retirement
            <InfoIcon tooltip="The number of years they expect to be in retirement. The average life expectancy calculated based on Australian Government Actuary Life Tables 2020-22 with 25-year improvement factors for a 67-year-old male is 87.7, and for a female is 89.8, and a for a 67-year-old couple their life expectancy (the age at which the last person will pass away on average) is 94." />
          </label>
          <input
            type="number"
            className="form-control"
            id="years"
            min="1"
            max="60"
            value={formData.years || ''}
            onChange={(e) => updateFormData({ years: parseInt(e.target.value) || 0 })}
          />
        </div>
        )}
        {!isAlreadyRetired && (
          <div className="col-md-3">
            <label htmlFor="annual_contributions" className="form-label small fw-semibold" style={{ color: '#072040' }}>
              Annual Contributions ($)
              <InfoIcon tooltip="Annual contribution to the portfolio before retirement. Only applies if retirement age is greater than current age." />
            </label>
            <input
              type="number"
              className="form-control"
              id="annual_contributions"
              value={formData.annual_contributions || ''}
              placeholder="0"
              min="0"
              step="1000"
              onChange={(e) => updateFormData({ annual_contributions: parseFloat(e.target.value) || 0 })}
            />
          </div>
        )}
      </div>

      {!hiddenFields.includes('retirement_age') && (
      <p className="small text-muted mb-3" style={{ fontStyle: 'italic' }}>
        If already in retirement, set retirement age as current age
      </p>
      )}

      {/* Row 3: Investment Portfolio + Non-Financial Assets + Blended Tax Rate */}
      <div className="row mb-2">
        <div className="col-md-4">
          <label htmlFor="financial_assets" className="form-label small fw-semibold" style={{ color: '#072040' }}>
            Investment Portfolio ($)
            <InfoIcon tooltip="Total value of your investment portfolio including superannuation, shares, bonds, term deposits, and other investments (excluding your home and annuity purchase amount)." />
          </label>
          <input
            type="number"
            className="form-control"
            id="financial_assets"
            value={formData.financial_assets || ''}
            min="0"
            step="1000"
            placeholder="0"
            onChange={(e) => updateFormData({ financial_assets: parseFloat(e.target.value) || 0 })}
          />
        </div>
        <div className="col-md-4">
          <label htmlFor="non_financial_assets" className="form-label small fw-semibold" style={{ color: '#072040' }}>
            Non-Financial Assets ($)
            <InfoIcon tooltip="Value of business assets, collectibles, and other non-financial investments that may provide income or capital growth (excluding investment properties)." />
          </label>
          <input
            type="number"
            className="form-control"
            id="non_financial_assets"
            value={formData.non_financial_assets || ''}
            min="0"
            step="1000"
            placeholder="0"
            onChange={(e) => updateFormData({ non_financial_assets: parseFloat(e.target.value) || 0 })}
          />
        </div>
        <div className="col-md-4">
          <label htmlFor="blended_tax_rate" className="form-label small fw-semibold" style={{ color: '#072040' }}>
            Blended Tax Rate (%)
            <InfoIcon tooltip="Your expected blended tax rate on investment returns. This is applied to calculate after-tax expected returns. Consider your marginal tax rate and the mix of income types (capital gains, dividends, interest)." />
          </label>
          <input
            type="number"
            className="form-control"
            id="blended_tax_rate"
            min="0"
            max="100"
            step="0.5"
            value={formData.blended_tax_rate || ''}
            placeholder="0"
            onChange={(e) => updateFormData({ blended_tax_rate: parseFloat(e.target.value) || 0 })}
          />
        </div>
      </div>

      {/* Lifetime Annuity */}
      <SectionBanner
        icon="bi bi-graph-up-arrow"
        title="Lifetime Annuity"
        color="#599BE3"
        tooltip="For couples, the annuity is assumed to be owned by the oldest member."
      />

      <div className="row mb-3">
        <div className="col-md-5">
          <label className="form-label small fw-semibold" style={{ color: '#072040' }}>
            Annuity Type
            <InfoIcon tooltip="Select the type of annuity. Inflation adjusted maintains constant real payments. Market linked payments vary with the selected reference portfolio returns." />
          </label>
          <select
            className="form-select form-control"
            value={formData.annuity_type || 'inflation_adjusted'}
            onChange={handleAnnuityTypeChange}
          >
            <option value="inflation_adjusted">Inflation Adjusted</option>
            <option value="market_linked">Market Linked</option>
          </select>
        </div>
      </div>

      {formData.annuity_type === 'market_linked' && (
        <div className="row mb-3">
          <div className="col-md-5">
            <label className="form-label small fw-semibold" style={{ color: '#072040' }}>
              Reference Portfolio
              <InfoIcon tooltip="Market-linked annuities index payments annually based on the performance of the selected reference portfolio. Each year, the annuity payment adjusts by the portfolio's return (with 100% participation), meaning clients benefit fully from positive returns but also experience payment reductions in negative return years." />
            </label>
            <select
              className="form-select form-control"
              value={formData.annuity_reference_portfolio || ''}
              onChange={(e) => updateFormData({ annuity_reference_portfolio: e.target.value })}
            >
              <option value="">-- Select Reference Portfolio --</option>
              {REFERENCE_PORTFOLIOS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
          <div className="col-md-5">
            <label className="form-label small fw-semibold" style={{ color: '#072040' }}>
              Accelerated Payment Reduction Rate (%)
              <InfoIcon tooltip="Choose to receive higher starting payments in exchange for lower future indexation. Enter a value between 0% (no acceleration) and 8% (maximum acceleration). Each year, this percentage will be deducted from the market return before indexing your payment." />
            </label>
            <input
              type="number"
              className="form-control"
              value={formData.accelerated_reduction_rate || 0}
              min="0"
              max="8"
              step="1"
              onChange={(e) => updateFormData({ accelerated_reduction_rate: parseInt(e.target.value) || 0 })}
            />
            <small className="text-muted">Higher values = higher starting payments but lower future growth. 0% = standard market-linked indexation.</small>
          </div>
        </div>
      )}

      <div className="row mb-3">
        <div className="col-md-5">
          <label className="form-label small fw-semibold" style={{ color: '#072040' }}>
            Annuity Purchase Amount ($)
            <InfoIcon tooltip="The total amount they paid to purchase the lifetime annuity." />
          </label>
          <input
            type="number"
            className="form-control"
            value={formData.income_stream_asset_test || ''}
            placeholder="0"
            min="0"
            step="1000"
            onChange={(e) => updateFormData({ income_stream_asset_test: parseFloat(e.target.value) || 0 })}
          />
        </div>
        <div className="col-md-5">
          <label className="form-label small fw-semibold" style={{ color: '#072040' }}>
            Annual Payment Amount ($)
            <InfoIcon tooltip="The annual payment amount they receive from the annuity. For market-linked annuities, this is the initial payment which will fluctuate based on reference portfolio returns." />
          </label>
          <input
            type="number"
            className="form-control"
            value={formData.income_stream || ''}
            placeholder="0"
            min="0"
            step="1000"
            onChange={(e) => updateFormData({ income_stream: parseFloat(e.target.value) || 0 })}
          />
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-5">
          <label className="form-label small fw-semibold" style={{ color: '#072040' }}>
            Payment Commencement Age
            <InfoIcon tooltip="The age when annuity payments commence and the income test applies. The asset test applies from purchase date." />
          </label>
          <input
            type="number"
            className="form-control"
            value={formData.annuity_commencement_age || ''}
            placeholder="67"
            min="50"
            max="100"
            onChange={(e) => updateFormData({ annuity_commencement_age: parseInt(e.target.value) || 0 })}
          />
        </div>
      </div>

      {/* ─── Sub-section 3: Other Income ─── */}

      {/* Investment Properties */}
      <SectionBanner
        icon="bi bi-building"
        title="Investment Properties"
        color="#2AADAD"
      />

      <div className="row mb-3 align-items-end">
        <div className="col-md-5">
          <label className="form-label small fw-semibold" style={{ color: '#072040' }}>
            Net Rental Income ($)
            <InfoIcon tooltip="Annual net rental income received during retirement (after expenses and tax). This is the net cash flow from rental properties." />
          </label>
          <input
            type="number"
            className="form-control"
            value={formData.net_rental_income || ''}
            placeholder="0"
            min="0"
            step="1000"
            onChange={(e) => updateFormData({ net_rental_income: parseFloat(e.target.value) || 0 })}
          />
        </div>
        <div className="col-md-4">
          <label className="form-label small fw-semibold" style={{ color: '#072040' }}>
            Years in retirement investment property is held
            <InfoIcon tooltip="Number of years in retirement the investment property is held before being sold." />
          </label>
          <input
            type="number"
            className="form-control"
            value={formData.rental_income_years || ''}
            placeholder="0"
            min="0"
            max="50"
            disabled={formData.rental_entire_retirement}
            onChange={(e) => updateFormData({ rental_income_years: parseInt(e.target.value) || 0 })}
          />
        </div>
        <div className="col-md-3 d-flex align-items-center" style={{ paddingBottom: 4 }}>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="rental_entire_retirement"
              checked={formData.rental_entire_retirement || false}
              onChange={(e) => updateFormData({ rental_entire_retirement: e.target.checked })}
            />
            <label className="form-check-label small" htmlFor="rental_entire_retirement">
              Entire retirement
            </label>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-md-5">
          <label className="form-label small fw-semibold" style={{ color: '#072040' }}>
            Investment Property Value ($)
            <InfoIcon tooltip="Total value of investment properties. This will be included in the age pension asset test." />
          </label>
          <input
            type="number"
            className="form-control"
            value={formData.investment_property_value || ''}
            placeholder="0"
            min="0"
            step="1000"
            onChange={(e) => updateFormData({ investment_property_value: parseFloat(e.target.value) || 0 })}
          />
        </div>
      </div>

      {showPropertyReminder && (
        <div className="alert alert-info small mb-4" role="alert">
          <i className="bi bi-info-circle me-2"></i>
          Remember to add the investment property sale value as a lump sum contribution in year {rentalYears} (age {retirementAge + rentalYears}) when the property is sold.
        </div>
      )}

      {/* Other Income Sources */}
      <SectionBanner
        icon="bi bi-wallet2"
        title="Other Income Sources"
        color="#1B6B6B"
      />

      <p className="small text-muted mb-3" style={{ fontStyle: 'italic' }}>
        All income amounts below are entered as net of tax values.
      </p>

      <div className="row mb-1">
        <div className="col-md-5">
          <span className="small fw-bold" style={{ color: '#072040' }}>Income Sources</span>
        </div>
        <div className="col-md-4">
          <span className="small fw-bold" style={{ color: '#072040' }}>Length of Time in Retirement Income is Earned</span>
        </div>
      </div>

      <IncomeRow
        label="Employment Income ($)"
        amountKey="employment_income"
        yearsKey="work_years"
        entireKey="employment_entire_retirement"
        amountTooltip="Annual income from employment after reaching age pension eligibility age (67). This income is assessable for age pension purposes and taxation. Only applies from age 67 onwards."
        yearsTooltip="Number of years they plan to continue working after retirement. Employment income will continue for this many years from their retirement age, affecting age pension calculations when they reach age 67."
        formData={formData}
        updateFormData={updateFormData}
      />

      <IncomeRow
        label="Other Income ($)"
        amountKey="other_income"
        yearsKey="other_income_years"
        entireKey="other_income_entire_retirement"
        amountTooltip="Annual other income received during retirement (net of tax). This could include business income, trust distributions, or other regular income sources."
        yearsTooltip="Number of years they expect to receive this other income during retirement."
        formData={formData}
        updateFormData={updateFormData}
      />

      {/* Navigation */}
      <div className="btn-group mt-4 pt-3 border-top">
        <button
          className="btn text-white ms-2"
          style={{ backgroundColor: '#072040' }}
          onClick={nextStep}
        >
          Next
        </button>
      </div>
      </fieldset>
    </div>
  );
};

export default StepPersonalDetails;
